import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useMotionValueEvent, useTransform } from "framer-motion";
import { DEMO_SITES } from "@/lib/demo-sites";
import DemoSitePreview from "@/components/websites/DemoSitePreview";

const DESIGN_WIDTH = 1280;
const LAST = DEMO_SITES.length - 1;

export default function SiteScrollCarousel() {
    const pinRef = useRef(null);
    const frameRef = useRef(null);
    const [scale, setScale] = useState(0.55);
    const [active, setActive] = useState(0);
    const reel = useMotionValue(0);

    useMotionValueEvent(reel, "change", (value) => {
        const next = Math.round(Math.min(LAST, Math.max(0, value)));
        setActive((current) => (current === next ? current : next));
    });

    useEffect(() => {
        const el = pinRef.current;
        if (!el) return undefined;

        const update = () => {
            const range = el.offsetHeight - window.innerHeight;
            const progress = range > 0 ? -el.getBoundingClientRect().top / range : 0;
            reel.set(Math.min(1, Math.max(0, progress)) * LAST);
        };

        update();
        window.addEventListener("scroll", update, { passive: true });
        window.addEventListener("resize", update);

        let detachLenis = () => {};
        const attachLenis = () => {
            const lenis = window.__lenis;
            if (!lenis) return false;
            lenis.on("scroll", update);
            detachLenis = () => lenis.off("scroll", update);
            return true;
        };
        const retry = attachLenis() ? 0 : window.setTimeout(attachLenis, 60);

        return () => {
            window.removeEventListener("scroll", update);
            window.removeEventListener("resize", update);
            window.clearTimeout(retry);
            detachLenis();
        };
    }, [reel]);

    useEffect(() => {
        const el = frameRef.current;
        if (!el) return undefined;
        const measure = () => setScale(el.clientWidth / DESIGN_WIDTH);
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const current = DEMO_SITES[active];

    return (
        <section
            id="example-sites"
            data-testid="websites-site-carousel"
            ref={pinRef}
            className="relative border-t border-white/5"
            style={{ height: `${DEMO_SITES.length * 90}vh` }}
        >
            <div className="pointer-events-none sticky top-0 flex h-screen flex-col px-4 sm:px-8 lg:px-16 pt-24 pb-8">
                <div className="mx-auto flex w-full max-w-[1400px] min-h-0 flex-1 flex-col">
                    <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                        <div>
                            <p className="font-mono-tech mb-3 text-xs font-semibold uppercase tracking-[0.3em] text-[#FF3B30]">
                                Example sites
                            </p>
                            <h2 className="font-display text-3xl font-black uppercase tracking-tight text-white sm:text-4xl">
                                Six businesses. Six different sites.
                            </h2>
                            <p className="mt-3 max-w-xl text-sm text-slate-400 sm:text-base">
                                Keep scrolling the page. The sites change with the scrollbar — bakery,
                                law, gym, salon, clinic, landscape.
                            </p>
                        </div>
                        <div className="flex items-center gap-4">
                            <p className="font-mono-tech text-xs uppercase tracking-[0.2em] text-slate-500">
                                {String(active + 1).padStart(2, "0")} /{" "}
                                {String(DEMO_SITES.length).padStart(2, "0")}
                            </p>
                            <div>
                                <p className="font-display text-xl text-white">{current.name}</p>
                                <p className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                    {current.industry}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="grid min-h-0 flex-1 grid-cols-1 gap-6 lg:grid-cols-12">
                        <ol className="hidden flex-col justify-center gap-1 lg:col-span-3 lg:flex">
                            {DEMO_SITES.map((site, index) => (
                                <li key={site.id}>
                                    <div
                                        className={`flex items-baseline gap-3 border-l-2 px-4 py-3 transition-colors duration-300 ${
                                            index === active
                                                ? "border-[#FF3B30] text-white"
                                                : "border-white/10 text-slate-600"
                                        }`}
                                    >
                                        <span className="font-mono-tech text-[10px] tracking-[0.2em]">
                                            {String(index + 1).padStart(2, "0")}
                                        </span>
                                        <span className="font-display text-lg">{site.name}</span>
                                    </div>
                                </li>
                            ))}
                        </ol>

                        <div className="flex min-h-0 items-center lg:col-span-9">
                            <div
                                ref={frameRef}
                                className="relative w-full overflow-clip rounded-2xl border border-white/10 bg-[#0E121B] shadow-[0_30px_80px_rgba(0,0,0,0.45)]"
                                style={{ height: Math.max(320, 820 * scale) }}
                            >
                                <div className="flex items-center gap-2 border-b border-white/10 px-4 py-2.5">
                                    <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
                                    <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
                                    <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
                                    <span className="ml-3 flex-1 truncate rounded-full bg-white/5 px-3 py-1 font-mono-tech text-[10px] tracking-[0.16em] text-slate-500">
                                        {current.domain}
                                    </span>
                                </div>
                                <div className="relative" style={{ perspective: 1600 }}>
                                    {DEMO_SITES.map((site, index) => (
                                        <CarouselSlide
                                            key={site.id}
                                            site={site}
                                            index={index}
                                            reel={reel}
                                            scale={scale}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function CarouselSlide({ site, index, reel, scale }) {
    const rotateY = useTransform(reel, (value) => (value - index) * -32);
    const x = useTransform(reel, (value) => (value - index) * 70);
    const opacity = useTransform(reel, (value) => {
        const d = Math.abs(value - index);
        return d < 0.85 ? 1 - d * 0.85 : 0;
    });
    const z = useTransform(reel, (value) => -Math.abs(value - index) * 140);
    const siteScale = useTransform(reel, (value) => 1 - Math.abs(value - index) * 0.06);
    const zIndex = useTransform(reel, (value) => (Math.abs(value - index) < 0.55 ? 3 : 1));

    return (
        <motion.div
            style={{
                rotateY,
                x,
                z,
                opacity,
                scale: siteScale,
                zIndex,
                transformStyle: "preserve-3d",
            }}
            className="absolute inset-x-0 top-10 origin-center"
        >
            <div
                style={{
                    width: DESIGN_WIDTH,
                    transform: `scale(${scale})`,
                    transformOrigin: "top left",
                }}
            >
                <DemoSitePreview site={site} />
            </div>
        </motion.div>
    );
}
